import React, { useState } from 'react';
import { X, Send, AlertCircle, Pin, Globe } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

interface AdminPostCreatorProps {
  isOpen: boolean;
  onClose: () => void;
  onPostCreated?: () => void;
}

const AdminPostCreator: React.FC<AdminPostCreatorProps> = ({
  isOpen,
  onClose,
  onPostCreated
}) => {
  const [content, setContent] = useState('');
  const [sport, setSport] = useState('General');
  const [priority, setPriority] = useState('normal');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sportOptions = [
    'General', 'Football', 'Cricket', 'Tennis', 'Chess', 'Basketball', 
    'Swimming', 'Badminton', 'Athletics', 'Volleyball', 'Hockey', 
    'Boxing', 'Wrestling', 'Gymnastics', 'Cycling'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      toast.error('Please enter post content');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await api.post('/posts/admin', {
        content: content.trim(),
        sport,
        priority
      });

      if (response.data.success) {
        toast.success('Admin post created successfully! 🎉');
        setContent('');
        setSport('General');
        setPriority('normal');
        onPostCreated?.();
        onClose();
      }
    } catch (error: any) {
      console.error('Error creating admin post:', error);
      toast.error(error.response?.data?.error?.message || 'Failed to create post');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setContent('');
      setSport('General');
      setPriority('normal');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-red-500 text-white flex items-center justify-center font-bold">
              A
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Create Admin Post</h2>
              <p className="text-sm text-gray-500">Post as administrator to all users</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Admin Notice */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Admin Post Notice</p>
              <p>This post will appear in all users' feeds with an "ADMIN" badge and will be visible to everyone on the platform.</p>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Post Content *
            </label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="What would you like to announce to the community?"
              className="w-full h-32 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={2000}
              required
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>This will be posted as an official admin announcement</span>
              <span>{content.length}/2000</span>
            </div>
          </div>

          {/* Sport Category */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Sport Category
            </label>
            <select
              value={sport}
              onChange={(e) => setSport(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {sportOptions.map((sportOption) => (
                <option key={sportOption} value={sportOption}>
                  {sportOption}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Post Priority
            </label>
            <div className="space-y-2">
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="priority"
                  value="normal"
                  checked={priority === 'normal'}
                  onChange={(e) => setPriority(e.target.value)}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <div className="flex items-center space-x-2">
                  <Globe className="w-4 h-4 text-gray-500" />
                  <div>
                    <span className="font-medium text-gray-900">Normal</span>
                    <p className="text-sm text-gray-500">Regular admin post in feed</p>
                  </div>
                </div>
              </label>
              <label className="flex items-center space-x-3">
                <input
                  type="radio"
                  name="priority"
                  value="high"
                  checked={priority === 'high'}
                  onChange={(e) => setPriority(e.target.value)}
                  className="text-red-600 focus:ring-red-500"
                />
                <div className="flex items-center space-x-2">
                  <Pin className="w-4 h-4 text-red-500" />
                  <div>
                    <span className="font-medium text-gray-900">High Priority (Pinned)</span>
                    <p className="text-sm text-gray-500">Pin to top of all users' feeds</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!content.trim() || isSubmitting}
              className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Send className="w-4 h-4" />
              <span>{isSubmitting ? 'Posting...' : 'Post as Admin'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminPostCreator;